import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { Bell, Send, Users, Building, AlertTriangle, CheckCircle, Globe } from 'lucide-react'

interface Tenant {
  id: string
  name: string
}

interface SystemPushNotificationManagerProps {
  tenants?: Tenant[]
}

export function SystemPushNotificationManager({ tenants = [] }: SystemPushNotificationManagerProps) {
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [sendToAll, setSendToAll] = useState(true)
  const [selectedTenant, setSelectedTenant] = useState<string>('')
  const [sendToRole, setSendToRole] = useState<'all' | 'admin' | 'customer'>('all')
  const [priority, setPriority] = useState<'normal' | 'high'>('normal')
  const [loading, setLoading] = useState(false)
  const [lastResult, setLastResult] = useState<{
    success?: boolean
    message?: string
    successful?: number
    failed?: number
  } | null>(null)

  const { toast } = useToast()

  const handleSendNotification = async () => {
    if (!title.trim() || !message.trim()) {
      toast({
        title: "Eingabe erforderlich",
        description: "Bitte geben Sie einen Titel und eine Nachricht ein.",
        variant: "destructive"
      })
      return
    }

    if (!sendToAll && !selectedTenant) {
      toast({
        title: "Unternehmen auswählen",
        description: "Bitte wählen Sie ein Unternehmen aus oder aktivieren Sie 'An alle senden'.",
        variant: "destructive"
      })
      return
    }

    setLoading(true)
    setLastResult(null)

    try {
      const payload = {
        title: title.trim(),
        message: message.trim(),
        system_wide: sendToAll,
        tenant_id: sendToAll ? null : selectedTenant,
        target_role: sendToRole,
        priority,
        type: 'system_announcement'
      }

      const { data, error } = await supabase.functions.invoke('send-push-notification', {
        body: payload
      })

      if (error) {
        throw new Error(error.message || 'Fehler beim Senden der Benachrichtigung')
      }

      setLastResult(data)
      
      if (data?.success) {
        toast({
          title: "Benachrichtigung gesendet",
          description: `Erfolgreich an ${data.successful || 0} Empfänger gesendet.`
        })
        
        // Reset form on success
        setTitle('')
        setMessage('')
        setSelectedTenant('')
        setSendToRole('all')
        setPriority('normal')
      } else {
        throw new Error(data?.error || 'Unbekannter Fehler beim Senden')
      }

    } catch (error: any) {
      console.error('Error sending notification:', error)
      setLastResult({
        success: false,
        message: error.message
      })
      
      toast({
        title: "Fehler beim Senden",
        description: error.message || "Die Benachrichtigung konnte nicht gesendet werden.",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const getTargetDescription = () => {
    if (sendToAll) {
      if (sendToRole === 'all') return 'Alle Benutzer im System'
      if (sendToRole === 'admin') return 'Alle Administratoren'
      if (sendToRole === 'customer') return 'Alle Kunden'
    } else {
      const tenant = tenants.find(t => t.id === selectedTenant)
      const tenantName = tenant?.name || 'Ausgewähltes Unternehmen'
      if (sendToRole === 'all') return `Alle Benutzer von ${tenantName}`
      if (sendToRole === 'admin') return `Administratoren von ${tenantName}`
      if (sendToRole === 'customer') return `Kunden von ${tenantName}`
    }
    return 'Keine Zielgruppe ausgewählt'
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Systemweite Push-Benachrichtigungen
        </CardTitle>
        <CardDescription>
          Senden Sie wichtige Ankündigungen und Updates an Benutzer im gesamten System
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Title and Message */}
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label htmlFor="title">Titel der Benachrichtigung *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Wichtige Systemwartung"
              maxLength={50}
            />
            <p className="text-xs text-muted-foreground">
              {title.length}/50 Zeichen
            </p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="message">Nachricht *</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Beschreiben Sie hier die wichtige Information..."
              rows={4}
              maxLength={200}
            />
            <p className="text-xs text-muted-foreground">
              {message.length}/200 Zeichen
            </p>
          </div>
        </div>

        {/* Target Selection */}
        <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                An alle Benutzer senden
              </Label>
              <p className="text-sm text-muted-foreground">
                Systemweite Benachrichtigung an alle registrierten Benutzer
              </p>
            </div>
            <Switch
              checked={sendToAll}
              onCheckedChange={setSendToAll}
            />
          </div>

          {!sendToAll && (
            <div className="space-y-2">
              <Label htmlFor="tenant">Spezifisches Unternehmen</Label>
              <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                <SelectTrigger>
                  <SelectValue placeholder="Unternehmen auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        {tenant.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="role">Zielgruppe</Label>
            <Select value={sendToRole} onValueChange={(value: 'all' | 'admin' | 'customer') => setSendToRole(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Benutzer</SelectItem>
                <SelectItem value="admin">Nur Administratoren</SelectItem>
                <SelectItem value="customer">Nur Kunden</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority">Priorität</Label>
            <Select value={priority} onValueChange={(value: 'normal' | 'high') => setPriority(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="high">Hoch (Wichtige Ankündigung)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Target Preview */}
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm font-medium text-blue-900">Zielgruppe:</p>
          <p className="text-sm text-blue-700">{getTargetDescription()}</p>
          {priority === 'high' && (
            <div className="flex items-center gap-1 mt-1">
              <AlertTriangle className="h-3 w-3 text-orange-600" />
              <span className="text-xs text-orange-600">Hohe Priorität</span>
            </div>
          )}
        </div>

        {/* Send Button */}
        <Button 
          onClick={handleSendNotification} 
          disabled={loading || !title.trim() || !message.trim()}
          className="w-full"
          size="lg"
        >
          <Send className="mr-2 h-4 w-4" />
          {loading ? 'Sende Benachrichtigung...' : 'Benachrichtigung senden'}
        </Button>

        {/* Result Display */}
        {lastResult && (
          <Alert className={lastResult.success ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"}>
            <div className="flex items-center gap-2">
              {lastResult.success ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-red-600" />
              )}
              <AlertDescription className={lastResult.success ? "text-green-800" : "text-red-800"}>
                {lastResult.success ? (
                  <div>
                    <p className="font-medium">Benachrichtigung erfolgreich gesendet!</p>
                    <p className="text-sm">
                      ✅ {lastResult.successful || 0} erfolgreich gesendet
                      {(lastResult.failed || 0) > 0 && (
                        <span> • ❌ {lastResult.failed} fehlgeschlagen</span>
                      )}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="font-medium">Fehler beim Senden</p>
                    <p className="text-sm">{lastResult.message}</p>
                  </div>
                )}
              </AlertDescription>
            </div>
          </Alert>
        )}

        {/* Usage Notes */}
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm font-medium mb-2">💡 Verwendungshinweise:</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Systemweite Benachrichtigungen sollten sparsam verwendet werden</li>
            <li>• Hohe Priorität nur für kritische Ankündigungen verwenden</li>
            <li>• Kurze, prägnante Nachrichten funktionieren am besten</li>
            <li>• Benutzer können Push-Benachrichtigungen in ihren Einstellungen deaktivieren</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  )
}